import React from 'react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../../components/ui/dialog';
import { toast } from 'sonner';
import { eaApi } from '../../lib/api';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import { motion } from 'framer-motion';
import {
  Shield,
  Users,
  Plus,
  Search,
  Trash2,
  Edit,
  UserPlus,
  Gamepad2,
  Link2,
  Unlink,
  Loader2,
} from 'lucide-react';

export const ClubsTab = ({
  isItalian,
  clubs,
  users,
  clubSearchQuery,
  setClubSearchQuery,
  handleOpenCreateClub,
  handleViewRoster,
  handleEditClub,
  handleDeleteClub,
  handleOpenAddMember,
}) => {
  const [eaDialogClub, setEaDialogClub] = React.useState(null);
  const [eaSearch, setEaSearch] = React.useState('');
  const [eaResults, setEaResults] = React.useState([]);
  const [eaLoading, setEaLoading] = React.useState(false);
  const [eaLinkOverride, setEaLinkOverride] = React.useState({});

  const getEaLink = (club) => (club.club_id in eaLinkOverride ? eaLinkOverride[club.club_id] : club.ea_link);

  const handleEaSearch = async () => {
    if (eaSearch.trim().length < 2) return;
    setEaLoading(true);
    try {
      const res = await eaApi.search(eaSearch.trim());
      setEaResults(res.data?.results || []);
      if (!(res.data?.results || []).length) toast.info(isItalian ? 'Nessun club EA trovato' : 'No EA club found');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore ricerca EA');
    } finally {
      setEaLoading(false);
    }
  };

  const handleEaLink = async (result) => {
    try {
      const res = await eaApi.linkClub(eaDialogClub.club_id, {
        ea_club_id: result.ea_club_id,
        ea_club_name: result.name,
      });
      setEaLinkOverride(prev => ({ ...prev, [eaDialogClub.club_id]: res.data.ea_link }));
      toast.success(res.data.message);
      setEaDialogClub(null);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore collegamento');
    }
  };

  const handleEaUnlink = async () => {
    try {
      await eaApi.unlinkClub(eaDialogClub.club_id);
      setEaLinkOverride(prev => ({ ...prev, [eaDialogClub.club_id]: null }));
      toast.success(isItalian ? 'Club scollegato da EA' : 'Club unlinked from EA');
      setEaDialogClub(null);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore');
    }
  };

  const filteredClubs = clubs.filter(club => {
    if (!clubSearchQuery) return true;
    const query = clubSearchQuery.toLowerCase();
    const captainFromList = users.find(u => u.user_id === club.captain_id);
    const capName = club.captain_name || captainFromList?.name || '';
    const capGT = club.captain_gamertag || captainFromList?.gamertag || '';
    return (
      club.name?.toLowerCase().includes(query) ||
      capName.toLowerCase().includes(query) ||
      capGT.toLowerCase().includes(query)
    );
  });

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="bg-[#0a0d15] border border-white/10"
    >
      <div className="p-4 border-b border-white/10">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <h3 className="font-heading text-lg uppercase tracking-wider text-white">
            {isItalian ? 'Gestione Club' : 'Club Management'} ({clubs.length})
          </h3>
          <div className="flex items-center gap-3">
            <Button
              onClick={handleOpenCreateClub}
              className="bg-green-600 hover:bg-green-700 text-white"
              data-testid="create-club-btn"
            >
              <Plus size={16} className="mr-2" />
              {isItalian ? 'Crea Club' : 'Create Club'}
            </Button>
            <div className="relative w-full md:w-80">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
              <input
                type="text"
                placeholder={isItalian ? 'Cerca club per nome o capitano...' : 'Search by club name or captain...'}
                value={clubSearchQuery}
                onChange={(e) => setClubSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 bg-[#050508] border border-white/10 text-white placeholder-slate-500 focus:outline-none focus:border-[#00F0FF]"
                data-testid="club-search-input"
              />
            </div>
          </div>
        </div>
      </div>

      <div className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow className="border-white/10 hover:bg-transparent">
              <TableHead className="text-slate-500 uppercase tracking-wider text-xs">Club</TableHead>
              <TableHead className="text-slate-500 uppercase tracking-wider text-xs">{isItalian ? 'Capitano' : 'Captain'}</TableHead>
              <TableHead className="text-slate-500 uppercase tracking-wider text-xs text-right">Coins</TableHead>
              <TableHead className="text-slate-500 uppercase tracking-wider text-xs text-right">{isItalian ? 'Giocatori' : 'Players'}</TableHead>
              <TableHead className="text-slate-500 uppercase tracking-wider text-xs text-right">{isItalian ? 'Azioni' : 'Actions'}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredClubs.map((club) => {
              const captainFromList = users.find(u => u.user_id === club.captain_id);
              // Prefer backend-embedded fields (reliable) over the paginated
              // users list (may not contain this captain if they're not on the
              // first page of the users tab).
              const capName = club.captain_name || captainFromList?.name || '';
              const capGT = club.captain_gamertag || captainFromList?.gamertag || '';
              return (
                <TableRow key={club.club_id} className="border-white/10 hover:bg-white/5">
                  <TableCell>
                    <div className="flex items-center gap-3">
                      {club.logo ? (
                        <img src={club.logo} alt={club.name} className="w-10 h-10 rounded object-cover" loading="lazy" />
                      ) : (
                        <div
                          className="w-10 h-10 rounded flex items-center justify-center"
                          style={{ backgroundColor: club.colors?.primary || '#2979FF' }}
                        >
                          <Shield size={18} className="text-white" />
                        </div>
                      )}
                      <span className="font-medium text-white">{club.name}</span>
                    </div>
                  </TableCell>
                  <TableCell>
                    <div>
                      <p className="text-white">{capName || '-'}</p>
                      {capGT && (
                        <p className="text-sm text-[#00F0FF]">{capGT}</p>
                      )}
                    </div>
                  </TableCell>
                  <TableCell className="text-right font-mono text-[#FFEA00]">
                    {club.coins?.toLocaleString()}
                  </TableCell>
                  <TableCell className="text-right font-mono text-white">
                    {club.players?.length || 0}
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-1">
                      <Button
                        size="icon"
                        variant="ghost"
                        className="h-8 w-8 text-green-400 hover:text-green-400 hover:bg-green-400/10"
                        onClick={() => handleViewRoster(club)}
                        title={isItalian ? 'Vedi Roster' : 'View Roster'}
                        data-testid={`view-roster-${club.club_id}`}
                      >
                        <Users size={14} />
                      </Button>
                      <Button
                        size="icon"
                        variant="ghost"
                        className="h-8 w-8 text-[#FFEA00] hover:text-[#FFEA00] hover:bg-[#FFEA00]/10"
                        onClick={() => handleOpenAddMember(club)}
                        title={isItalian ? 'Aggiungi Membro' : 'Add Member'}
                        data-testid={`add-member-${club.club_id}`}
                      >
                        <UserPlus size={14} />
                      </Button>
                      <Button
                        size="icon"
                        variant="ghost"
                        className="h-8 w-8 text-[#00F0FF] hover:text-[#00F0FF] hover:bg-[#00F0FF]/10"
                        onClick={() => handleEditClub(club)}
                        title={isItalian ? 'Modifica Club' : 'Edit Club'}
                        data-testid={`edit-club-${club.club_id}`}
                      >
                        <Edit size={14} />
                      </Button>
                      <Button
                        size="icon"
                        variant="ghost"
                        className={`h-8 w-8 ${getEaLink(club) ? 'text-[#00E676] hover:text-[#00E676] hover:bg-[#00E676]/10' : 'text-slate-400 hover:text-white hover:bg-white/10'}`}
                        onClick={() => { setEaDialogClub(club); setEaSearch(club.name || ''); setEaResults([]); }}
                        title={getEaLink(club) ? `EA: ${getEaLink(club).ea_club_name}` : (isItalian ? 'Collega a EA Pro Clubs' : 'Link to EA Pro Clubs')}
                        data-testid={`ea-link-${club.club_id}`}
                      >
                        <Gamepad2 size={14} />
                      </Button>
                      <Button
                        size="icon"
                        variant="ghost"
                        className="h-8 w-8 text-[#FF1744] hover:text-[#FF1744] hover:bg-[#FF1744]/10"
                        onClick={() => handleDeleteClub(club.club_id, club.name)}
                        title={isItalian ? 'Elimina Club' : 'Delete Club'}
                        data-testid={`delete-club-${club.club_id}`}
                      >
                        <Trash2 size={14} />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              );
            })}
            {filteredClubs.length === 0 && (
              <TableRow>
                <TableCell colSpan={5} className="text-center py-8 text-slate-500">
                  {clubSearchQuery
                    ? (isItalian ? 'Nessun club trovato' : 'No clubs found')
                    : (isItalian ? 'Nessun club registrato' : 'No clubs registered')
                  }
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      {/* EA Pro Clubs link dialog */}
      <Dialog open={!!eaDialogClub} onOpenChange={(o) => { if (!o) setEaDialogClub(null); }}>
        <DialogContent className="bg-[#0a0d15] border-white/10 text-white max-w-lg" data-testid="ea-link-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading uppercase tracking-wider flex items-center gap-2">
              <Gamepad2 size={18} className="text-[#00F0FF]" />
              {isItalian ? 'Collega a EA Pro Clubs' : 'Link to EA Pro Clubs'}
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              {eaDialogClub?.name} — {isItalian ? 'cerca il club su EA e collegalo per le statistiche e l\'auto-report' : 'search the EA club and link it for stats and auto-report'}
            </DialogDescription>
          </DialogHeader>

          {eaDialogClub && getEaLink(eaDialogClub) && (
            <div className="flex items-center justify-between bg-[#00E676]/10 border border-[#00E676]/30 rounded p-3">
              <div className="text-sm">
                <p className="text-[#00E676] font-bold flex items-center gap-1"><Link2 size={14} /> {getEaLink(eaDialogClub).ea_club_name}</p>
                <p className="text-[10px] text-slate-500 font-mono">EA ID: {getEaLink(eaDialogClub).ea_club_id}</p>
              </div>
              <Button size="sm" variant="outline" className="border-[#FF1744]/40 text-[#FF1744] hover:bg-[#FF1744]/10" onClick={handleEaUnlink} data-testid="ea-unlink-btn">
                <Unlink size={14} className="mr-1" /> {isItalian ? 'Scollega' : 'Unlink'}
              </Button>
            </div>
          )}

          <div className="flex gap-2">
            <Input
              value={eaSearch}
              onChange={(e) => setEaSearch(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleEaSearch()}
              placeholder={isItalian ? 'Nome club su EA...' : 'EA club name...'}
              className="bg-[#050508] border-white/10 text-white"
              data-testid="ea-search-input"
            />
            <Button onClick={handleEaSearch} disabled={eaLoading || eaSearch.trim().length < 2} className="bg-[#00F0FF] text-black hover:bg-[#00F0FF]/85" data-testid="ea-search-btn">
              {eaLoading ? <Loader2 size={14} className="animate-spin" /> : <Search size={14} />}
            </Button>
          </div>

          <div className="max-h-64 overflow-y-auto space-y-1">
            {eaResults.map((r) => (
              <div key={r.ea_club_id} className="flex items-center justify-between bg-[#050508] border border-white/10 rounded p-2 hover:border-[#00F0FF]/40 transition-colors">
                <div className="min-w-0">
                  <p className="text-sm text-white truncate">{r.name}</p>
                  <p className="text-[10px] text-slate-500 font-mono">
                    ID {r.ea_club_id} · {r.games_played} match · {r.wins}V {r.draws}N {r.losses}P
                  </p>
                </div>
                <Button size="sm" className="bg-[#00E676] text-black hover:bg-[#00E676]/85 flex-shrink-0 ml-2" onClick={() => handleEaLink(r)} data-testid={`ea-link-result-${r.ea_club_id}`}>
                  <Link2 size={13} className="mr-1" /> {isItalian ? 'Collega' : 'Link'}
                </Button>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </motion.div>
  );
};
